// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.

import './styles.css';
import type { AppContext, ViewRenderer } from './types';
import { loadData } from './data';
import { initTooltip } from './tooltip';
import { initGlossary, initAbout, openAbout } from './about';
import { initDrawer, openDrawer, closeDrawer } from './drilldown';

import { renderRankings } from './views/rankings';
import { renderExplorer } from './views/explorer';
import { renderMap } from './views/map';
import { renderCategories } from './views/categories';
import { renderParties } from './views/parties';
import { renderTrends } from './views/trends';
import { renderDistribution } from './views/distribution';
import { renderInsights } from './views/insights';

interface ViewDef {
  id: string;
  label: string;
  render: ViewRenderer;
}

const VIEWS: ViewDef[] = [
  { id: 'rankings', label: 'Rankings', render: renderRankings },
  { id: 'explorer', label: 'Explorer', render: renderExplorer },
  { id: 'map', label: 'Map', render: renderMap },
  { id: 'categories', label: 'Categories', render: renderCategories },
  { id: 'parties', label: 'Parties', render: renderParties },
  { id: 'trends', label: 'Trends', render: renderTrends },
  { id: 'distribution', label: 'Distribution', render: renderDistribution },
  { id: 'insights', label: 'Insights', render: renderInsights },
];
const VIEW_IDS = new Set(VIEWS.map((v) => v.id));

const app = document.getElementById('app')!;

function shell(): void {
  app.innerHTML = `
    <header class="site-header">
      <div class="header-inner">
        <div class="brand">
          <img src="/favicon.svg" alt="" width="34" height="34" />
          <div class="brand-text">
            <h1>Parliamentary Expenses</h1>
            <p>Australia's federal MPs &amp; Senators · official IPEA data</p>
          </div>
        </div>
        <div class="header-actions">
          <button class="header-btn" id="about-btn" aria-haspopup="dialog">About &amp; glossary</button>
        </div>
      </div>
    </header>
    <nav class="tabs" aria-label="Views">
      <div class="tabs-inner" id="tabs"></div>
    </nav>
    <main class="main-content" id="view-root" tabindex="-1"></main>
    <footer class="site-footer">
      <div class="footer-inner">
        <div class="footer-col">
          <h4>About</h4>
          <p>Every taxpayer-funded work expense claimed by Australian federal parliamentarians since the 2022 election, from the Independent Parliamentary Expenses Authority. Expenses are the cost of the job — travel, offices, staff, cars — and are separate from salary. Figures are published amounts, not allegations.</p>
        </div>
        <div class="footer-col">
          <h4>Source</h4>
          <p>IPEA "Current and Former Parliamentarians' Expenditure", via data.gov.au. Electorate boundaries: Digital Atlas of Australia — Federal Electoral Divisions (March 2025). Updated quarterly.</p>
        </div>
        <div class="footer-col">
          <h4>Built by</h4>
          <p>Built by <a href="https://benrichardson.dev/">benrichardson.dev</a> · <a href="https://sites.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a></p>
        </div>
      </div>
    </footer>

    <div class="scrim" id="drawer-scrim"></div>
    <aside class="panel-drawer" id="mp-drawer" role="dialog" aria-modal="true" aria-label="Parliamentarian detail"></aside>
    <div class="modal-scrim" id="about-scrim"></div>`;
}

let ctx: AppContext;
let currentView = '';

function renderTabs(active: string): void {
  const tabs = document.getElementById('tabs')!;
  tabs.innerHTML = VIEWS.map(
    (v) => `<button class="tab${v.id === active ? ' active' : ''}" data-view="${v.id}">${v.label}</button>`
  ).join('');
  tabs.querySelectorAll<HTMLButtonElement>('.tab').forEach((btn) => {
    btn.addEventListener('click', () => ctx.goto(btn.dataset.view!));
  });
}

function currentHashView(): string {
  const parts = location.hash.replace(/^#/, '').split('&');
  for (const p of parts) if (VIEW_IDS.has(p)) return p;
  return 'rankings';
}

function currentHashMp(): string | null {
  const parts = location.hash.replace(/^#/, '').split('&');
  for (const p of parts) if (p.startsWith('mp=')) return decodeURIComponent(p.slice(3));
  return null;
}

async function route(): Promise<void> {
  const view = currentHashView();
  const mp = currentHashMp();

  if (view !== currentView) {
    currentView = view;
    renderTabs(view);
    const root = document.getElementById('view-root')!;
    root.innerHTML = '<div class="state-msg"><div class="spinner"></div>Loading…</div>';
    const def = VIEWS.find((v) => v.id === view)!;
    try {
      await def.render(root, ctx);
    } catch (err) {
      root.innerHTML = `<div class="error-box">Something went wrong rendering this view.<br /><small>${(err as Error).message}</small></div>`;
    }
  }

  if (mp) {
    void openDrawer(mp, ctx.data);
  } else {
    closeDrawer();
  }
}

async function main(): Promise<void> {
  shell();
  initTooltip();
  initGlossary();

  const root = document.getElementById('view-root')!;
  root.innerHTML = '<div class="state-msg"><div class="spinner"></div>Loading parliamentary expenditure…</div>';

  let data;
  try {
    data = await loadData();
  } catch (err) {
    root.innerHTML = `<div class="error-box">Couldn't load the data.<br /><small>${(err as Error).message}</small><br /><button onclick="location.reload()">Retry</button></div>`;
    return;
  }

  ctx = {
    data,
    openMp: (slug: string) => {
      location.hash = `${currentView || 'rankings'}&mp=${encodeURIComponent(slug)}`;
    },
    goto: (view: string) => {
      location.hash = view;
    },
  };

  initAbout(data);
  initDrawer(data, () => {
    // Closing the drawer strips the mp param but keeps the view.
    location.hash = currentView || 'rankings';
  });
  document.getElementById('about-btn')!.addEventListener('click', openAbout);

  window.addEventListener('hashchange', () => void route());
  await route();
}

void main();
